import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  FaThLarge, FaBolt, FaImages, FaUsers, FaQuoteRight, FaBox, 
  FaProjectDiagram, FaHandshake, FaBlog, FaEnvelope, FaMailBulk, FaSignOutAlt,
  FaChevronLeft, FaChevronRight, FaChevronDown, FaBars
} from 'react-icons/fa';
import './styles/AdminLayout.css';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('adminUser') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/musa/login');
  };

  const menuItems = [
    { path: '/musa/dashboard', label: 'Dashboard', icon: FaThLarge },
    { path: '/musa/services', label: 'Services', icon: FaBolt },
    { path: '/musa/gallery', label: 'Gallery', icon: FaImages },
    { path: '/musa/team', label: 'Team', icon: FaUsers },
    { path: '/musa/testimonials', label: 'Reviews', icon: FaQuoteRight },
    { path: '/musa/products', label: 'Products', icon: FaBox },
    { path: '/musa/projects', label: 'Projects', icon: FaProjectDiagram },
    { path: '/musa/partners', label: 'Partners', icon: FaHandshake },
    { path: '/musa/blog', label: 'Blog', icon: FaBlog },
    { path: '/musa/contact', label: 'Contact', icon: FaEnvelope },
    { path: '/musa/subscriptions', label: 'Subs', icon: FaMailBulk },
    { path: '/musa/mails', label: 'Mails', icon: FaEnvelope }
  ];

  const desktopMenuItems = [
    { path: '/musa/dashboard', label: 'Dashboard', icon: FaThLarge },
    { path: '/musa/services', label: 'Our Services', icon: FaBolt },
    { path: '/musa/gallery', label: 'Gallery', icon: FaImages },
    { path: '/musa/team', label: 'Team', icon: FaUsers },
    { path: '/musa/testimonials', label: 'Testimonials', icon: FaQuoteRight },
    { path: '/musa/products', label: 'Products', icon: FaBox },
    { 
      label: 'Projects', icon: FaProjectDiagram,
      subItems: [
        { path: '/musa/projects', label: 'All Projects' },
        { path: '/musa/projects/categories', label: 'Project Categories' }
      ]
    },
    { 
      label: 'Partners', icon: FaHandshake,
      subItems: [
        { path: '/musa/partners', label: 'All Partners' },
        { path: '/musa/partners/create', label: 'Create Partner' }
      ]
    },
    { 
      label: 'Blog', icon: FaBlog,
      subItems: [
        { path: '/musa/blog', label: 'All Posts' },
        { path: '/musa/blog/categories', label: 'Blog Categories' }
      ]
    },
    { path: '/musa/contact', label: 'Contact', icon: FaEnvelope },
    { path: '/musa/subscriptions', label: 'Subscriptions', icon: FaMailBulk },
    { path: '/musa/mails', label: 'Mails', icon: FaEnvelope }
  ];

  // Start with all collapsed - click to expand
  const [expandedItems, setExpandedItems] = useState([]);

  const toggleExpand = (label) => {
    setExpandedItems(prev => 
      prev.includes(label) ? prev.filter(i => i !== label) : [...prev, label]
    );
  };

  return (
    <div className="admin-layout">
      {/* Desktop Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <Link to="/musa/dashboard" className="sidebar-logo">
            <img src={process.env.PUBLIC_URL + '/images/musa_logo.png'} alt="Musa Electrical" className="sidebar-logo-img" />
            {sidebarOpen && <span className="logo-text">Musa Electrical</span>}
          </Link>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FaChevronLeft /> : <FaChevronRight />}
          </button>
        </div>
        <nav className="sidebar-nav">
          {desktopMenuItems.map((item, index) => (
            <div key={index} className="nav-item-wrapper">
              {item.path ? (
                <Link to={item.path} className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}>
                  <item.icon className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">{item.label}</span>}
                </Link>
              ) : (
                <>
                  <button 
                    className={`nav-item has-sub ${expandedItems.includes(item.label) ? 'expanded' : ''}`}
                    onClick={() => toggleExpand(item.label)}
                  >
                    <item.icon className="nav-icon" />
                    {sidebarOpen && (
                      <>
                        <span className="nav-label">{item.label}</span>
                        <FaChevronDown className="nav-arrow" />
                      </>
                    )}
                  </button>
                  {expandedItems.includes(item.label) && sidebarOpen && (
                    <div className="sub-nav">
                      {item.subItems.map((sub, si) => (
                        <Link key={si} to={sub.path} className={`sub-nav-item ${location.pathname === sub.path ? 'active' : ''}`}>{sub.label}</Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <div className="admin-main">
        <header className="admin-header">
          <div className="header-left">
            <button className="mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}><FaBars /></button>
            <img src={process.env.PUBLIC_URL + '/images/musa_logo.png'} alt="Musa" className="mobile-logo" />
            <h2>Admin Panel</h2>
          </div>
          <div className="header-right">
            <div className="admin-avatar">
              <div className="avatar-circle">{user.name?.charAt(0) || 'A'}</div>
              <div className="avatar-info">
                <span className="avatar-name">{user.name || 'Admin'}</span>
                <span className="avatar-role">{user.role || 'Administrator'}</span>
              </div>
            </div>
            <button className="logout-btn" onClick={handleLogout}><FaSignOutAlt /> Logout</button>
          </div>
        </header>
        
        <div className="admin-content">
          <Outlet />
        </div>

        {/* Mobile Bottom Tabs */}
        <nav className="mobile-tabs">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path} className={`mobile-tab ${location.pathname === item.path ? 'active' : ''}`}>
              <item.icon />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default AdminLayout;
